/**
 * The single place that decides "the app is going away, save state now."
 *
 * `unload`/`beforeunload` are not reliable save points -- iOS kills a
 * backgrounded PWA without firing them at all. `visibilitychange` (only the
 * transition to `hidden`) and `pagehide` are the two events guaranteed to
 * fire first, so both are wired -- a tab that is backgrounded and then
 * killed still leaves state behind via whichever one lands.
 *
 * Framework-agnostic on purpose: `local-reporter.ts` and
 * `install-global-error-capture.ts` each installed this exact pair of
 * listeners by hand before this existed (each with a comment pointing at
 * the other as "the same two hooks"), and neither is a React component --
 * both are module-level singletons wired at boot. `useAppLifecycle`
 * (`@shared/lib/hooks`) is a thin React wrapper around this for consumers
 * that are components (reading position, scroll offset, player position).
 */
export function onAppHidden(callback: () => void): () => void {
	const onVisibilityChange = () => {
		if (document.visibilityState === 'hidden') {
			callback();
		}
	};

	document.addEventListener('visibilitychange', onVisibilityChange);
	window.addEventListener('pagehide', callback);

	return () => {
		document.removeEventListener('visibilitychange', onVisibilityChange);
		window.removeEventListener('pagehide', callback);
	};
}
